/**
 * Debug utility for tracking Character Generation flow
 * Helps identify where the process breaks
 */

import { supabase } from '../lib/supabase-jsonb';

interface CharacterGeneration {
  id: string;
  user_id: string;
  status: string;
  images: string[];
  result_url: string | null;
  error_message: string | null;
  runpod_job_id: string | null;
  session_id: string | null;
  created_at: string;
  updated_at: string;
}

interface DebugEvent {
  timestamp: Date;
  step: string;
  status: 'success' | 'error' | 'warning';
  details: any;
}

class CharacterGenerationDebugger {
  private events: DebugEvent[] = [];
  private generationId: string | null = null;
  private debugClient = supabase as any;

  startDebug(generationId: string) {
    this.generationId = generationId;
    this.events = [];
    this.log('debug_start', 'success', { generationId });
    
    // Store in localStorage for persistence
    this.saveToStorage();
    
    console.log('%c[DEBUG] Character Generation Started', 'background: #4CAF50; color: white; padding: 2px 4px;', {
      generationId,
      timestamp: new Date().toISOString()
    });
  }

  log(step: string, status: 'success' | 'error' | 'warning', details: any) {
    const event: DebugEvent = {
      timestamp: new Date(),
      step,
      status,
      details
    };
    
    this.events.push(event);
    
    // Color code console output
    const colors = {
      success: 'background: #4CAF50; color: white;',
      error: 'background: #f44336; color: white;',
      warning: 'background: #ff9800; color: white;'
    };
    
    console.log(
      `%c[${step.toUpperCase()}]`,
      colors[status] + ' padding: 2px 4px;',
      details
    );
    
    this.saveToStorage();
  }

  async checkDatabaseStatus(): Promise<CharacterGeneration | null> {
    if (!this.generationId) return null;
    
    try {
      const { data, error } = await this.debugClient
        .from('character_generations')
        .select('*')
        .eq('id', this.generationId)
        .single();
      
      if (error) {
        this.log('database_check', 'error', { error });
        return null;
      }
      
      const typedData = data as CharacterGeneration;
      if (typedData) {
        this.log('database_check', 'success', {
          status: typedData.status,
          runpod_job_id: typedData.runpod_job_id,
          result_url: typedData.result_url,
          updated_at: typedData.updated_at
        });
      }
      
      return typedData;
    } catch (error) {
      this.log('database_check', 'error', { error });
      return null;
    }
  }

  async checkWebhookLogs() {
    if (!this.generationId) return;
    
    try {
      // Check if webhook_logs table exists
      const { data, error } = await this.debugClient
        .from('webhook_logs')
        .select('*')
        .eq('generation_id', this.generationId)
        .order('created_at', { ascending: false });
      
      if (error) {
        this.log('webhook_logs', 'warning', { 
          message: 'webhook_logs table may not exist',
          error 
        });
      } else if (data && data.length > 0) {
        this.log('webhook_logs', 'success', { 
          count: data.length,
          latest: data[0] 
        });
      } else {
        this.log('webhook_logs', 'warning', { 
          message: 'No webhook logs found for this generation' 
        });
      }
      
      return data;
    } catch (error) {
      this.log('webhook_logs', 'error', { error });
      return null;
    }
  }

  async testRealtimeConnection() {
    if (!this.generationId) return;
    
    return new Promise((resolve) => {
      const handlePayload = (payload: { eventType?: string; new?: unknown }) => {
        this.log('realtime_test', 'success', {
          event: payload.eventType,
          data: payload.new
        });
      };

      const handleStatusChange = (status: string) => {
        if (status === 'SUBSCRIBED') {
          this.log('realtime_connection', 'success', { status });
          setTimeout(() => {
            this.debugClient.removeChannel(channel);
            resolve(true);
          }, 2000);
        } else if (status === 'CHANNEL_ERROR') {
          this.log('realtime_connection', 'error', { status });
          resolve(false);
        }
      };

      const channel = this.debugClient
        .channel(`test-${this.generationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'character_generations',
            filter: `id=eq.${this.generationId}`
          },
          handlePayload
        )
        .subscribe(handleStatusChange);
    });
  }

  async runFullDiagnostics() {
    console.log('%c=== RUNNING FULL DIAGNOSTICS ===', 'background: #2196F3; color: white; padding: 4px 8px; font-weight: bold;');
    
    // 1. Check database record
    const dbRecord = await this.checkDatabaseStatus();
    
    // 2. Check webhook logs
    await this.checkWebhookLogs();
    
    // 3. Test real-time connection
    await this.testRealtimeConnection();
    
    // 4. Check RunPod job status (if job ID exists)
    if (dbRecord?.runpod_job_id) {
      this.log('runpod_job', 'success', { 
        job_id: dbRecord.runpod_job_id,
        hint: 'Check RunPod dashboard for job status' 
      });
    } else {
      this.log('runpod_job', 'warning', { 
        message: 'No RunPod job ID found - edge function may not have triggered' 
      });
    }
    
    // 5. Generate report
    this.generateReport();
  }

  generateReport() {
    const report = {
      generationId: this.generationId,
      totalEvents: this.events.length,
      errors: this.events.filter(e => e.status === 'error'),
      warnings: this.events.filter(e => e.status === 'warning'),
      timeline: this.events.map(e => ({
        time: e.timestamp.toLocaleTimeString(),
        step: e.step,
        status: e.status
      }))
    };
    
    console.log('%c=== DIAGNOSTIC REPORT ===', 'background: #9C27B0; color: white; padding: 4px 8px; font-weight: bold;');
    console.table(report.timeline);
    
    if (report.errors.length > 0) {
      console.log('%c⚠️ ERRORS FOUND:', 'color: red; font-weight: bold;');
      report.errors.forEach(e => console.error(e.step, e.details));
    }
    
    if (report.warnings.length > 0) {
      console.log('%c⚠️ WARNINGS:', 'color: orange; font-weight: bold;');
      report.warnings.forEach(e => console.warn(e.step, e.details));
    }
    
    return report;
  }

  private saveToStorage() {
    if (this.generationId) {
      localStorage.setItem(
        `debug_character_gen_${this.generationId}`,
        JSON.stringify({
          generationId: this.generationId,
          events: this.events.map(e => ({
            ...e,
            timestamp: e.timestamp.toISOString()
          }))
        })
      );
    }
  }

  static loadFromStorage(generationId: string): CharacterGenerationDebugger | null {
    const data = localStorage.getItem(`debug_character_gen_${generationId}`);
    if (data) {
      const parsed = JSON.parse(data);
      const debugInstance = new CharacterGenerationDebugger();
      debugInstance.generationId = parsed.generationId;
      debugInstance.events = parsed.events.map((e: any) => ({
        ...e,
        timestamp: new Date(e.timestamp)
      }));
      return debugInstance;
    }
    return null;
  }
}

export default CharacterGenerationDebugger;

// Global instance for easy console access
if (typeof window !== 'undefined') {
  (window as any).CharGenDebug = CharacterGenerationDebugger;
}
